import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { camelizeKeys } from 'humps';
import { useQuery } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { DateTime } from 'luxon';

import { getData } from 'services/api';
import { FlexContainer } from 'features/common/components/FlexContainer';
import { DropdownInput } from '../DropdownInput';
import { InputLabel } from 'features/common/components/Form/InputLabel';
import { Event } from 'features/common/components/Filters/types';
import { getSelectedName } from '../../utils';
import { LabelProp } from '../../types';

import { FormText, ListContainer, Option, RadioButton } from '../../CampaignForm.styles';

export const Content = ({ label, isPunchup = false }: LabelProp & { isPunchup?: boolean }) => {
	const { t } = useTranslation();
	const [search, setSearch] = useState('');
	const [eventsPerPage, setPerPage] = useState(15);
	const { watch, setValue } = useFormContext();

	const eventFormValue = watch('focus');
	const [selectedEvents, setSelectedEvents] = useState<number[]>(eventFormValue || []);

	const { data: uncamelizedEventsData } = useQuery({
		queryKey: ['audience-events', eventsPerPage, search],
		queryFn: () => {
			const dateParam = search ? 'all' : 'live_or_scheduled';
			return getData(`/events?date=${dateParam}&per_page=${eventsPerPage}&search=${search}`);
		},
	});

	const eventsData = camelizeKeys(uncamelizedEventsData) as Event[];
	const isNotFullPage = eventsData?.length < 5 && eventsPerPage > 0;
	const updateValues = () => {
		setValue('focus', selectedEvents);
	};

	useEffect(() => {
		setSelectedEvents(eventFormValue || []);
	}, [eventFormValue]);

	const memoizedNames = useMemo(() => getSelectedName({ eventsData, eventFormValue }), [eventsData, eventFormValue]);

	return (
		<FlexContainer flexDirection="column" gap="16px">
			<FormText isPunchup={isPunchup}>{label}</FormText>
			<FlexContainer flexDirection="column" gap="8px">
				<InputLabel
					label={t('marketing-campaignForm-contentInfo')}
					htmlFor={label}
					color={isPunchup ? '#333' : undefined}
				/>
				<DropdownInput
					filterName={memoizedNames || t('marketing-campaignForm-selectFocus')}
					id={label}
					setPerPage={setPerPage}
					setSearch={setSearch}
					hideViewAll={eventsPerPage === -1 || isNotFullPage}
					updateValues={updateValues}
					isPunchup={isPunchup}
				>
					<ListContainer isPunchup={isPunchup}>
						{eventsData?.map(({ id, name, startDate, timeZone }) => (
							<Option key={id} isPunchup={isPunchup}>
								<RadioButton
									type="checkbox"
									checked={selectedEvents.includes(id)}
									name={`focus-${id}`}
									value={id}
									onChange={(e) => {
										if (e.target.checked) {
											setSelectedEvents([...selectedEvents, id]);
										} else {
											setSelectedEvents(selectedEvents.filter((eventId) => eventId !== id));
										}
									}}
								/>
								{`${name} - ${DateTime.fromISO(startDate).setZone(timeZone).toFormat('cccc, MMMM d - h:mma')} `}
							</Option>
						))}
					</ListContainer>
				</DropdownInput>
			</FlexContainer>
		</FlexContainer>
	);
};
